import React, { useMemo } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, darken, lighten } from '@mui/material/styles';

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      })
    : 'N/A';

const SectionTitle = ({ children, brand }) => (
  <Box sx={{ mb: 0.9 }}>
    <Typography
      variant="h5"
      sx={{
        fontFamily: '"Inter", "Poppins", sans-serif',
        fontWeight: 800,
        color: brand.ink,
      }}
    >
      {children}
    </Typography>
    <Box
      sx={{
        width: 190,
        height: 4,
        borderRadius: 999,
        mt: 1,
        background: `linear-gradient(90deg, ${brand.pDark}, ${brand.pMid} 60%, ${brand.pLight})`,
      }}
    />
  </Box>
);

const DatabaseBreachesPage = ({ primary = '#0A68FF', breachRows = [] }) => {
  const brand = {
    p: primary,
    pDark: darken(primary, 0.24),
    pMid: darken(primary, 0.12),
    pLight: lighten(primary, 0.28),
    ink: '#0f172a',
  };

  const rows = useMemo(() => {
    const values = Array.isArray(breachRows) ? [...breachRows] : [];
    values.sort((left, right) => {
      const leftDate = left?.date ? new Date(left.date).getTime() : 0;
      const rightDate = right?.date ? new Date(right.date).getTime() : 0;
      if (rightDate !== leftDate) return rightDate - leftDate;
      return (right?.impactedEmails || 0) - (left?.impactedEmails || 0);
    });
    return values;
  }, [breachRows]);

  return (
    <Box
      id="database-breaches"
      className="report-surface"
      sx={{
        maxWidth: '8.5in',
        mx: 'auto',
        py: '0.68in',
        px: '0.72in',
      }}
    >
      <Card
        className="avoid-break"
        sx={{
          border: `1px solid ${alpha(brand.ink, 0.08)}`,
          borderRadius: 4,
          boxShadow: 'none',
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)',
        }}
      >
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <SectionTitle brand={brand}>Database Breaches</SectionTitle>
          <Typography
            variant="body2"
            sx={{
              color: alpha(brand.ink, 0.72),
              lineHeight: 1.6,
              maxWidth: '92%',
            }}
          >
            Each row represents a unique breach database correlated to monitored identities. Counts reflect how many unique emails were impacted.
          </Typography>
        </Box>

        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& th, & td': {
              borderColor: alpha(brand.ink, 0.08),
              py: 1.15,
              px: 1.25,
              verticalAlign: 'top',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            },
            '& th': {
              fontFamily: '"Inter", "Poppins", sans-serif',
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#475569',
              bgcolor: alpha(primary, 0.05),
            },
            '& td': {
              fontSize: '0.88rem',
              lineHeight: 1.5,
              color: brand.ink,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '15%' }}>Name</TableCell>
              <TableCell sx={{ width: '11%' }}>Date</TableCell>
              <TableCell sx={{ width: '22%' }}>Data Leaked</TableCell>
              <TableCell sx={{ width: '10%' }}>Emails</TableCell>
              <TableCell sx={{ width: '42%' }}>Description</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length ? rows.map((row) => (
              <TableRow key={`${row.name}-${row.date || 'na'}`}>
                <TableCell sx={{ fontWeight: 700 }}>{row.name}</TableCell>
                <TableCell>{formatDate(row.date)}</TableCell>
                <TableCell>{row.dataClasses?.length ? row.dataClasses.join(', ') : 'N/A'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{row.impactedEmails || 0}</TableCell>
                <TableCell>{row.description || 'N/A'}</TableCell>
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={5} sx={{ color: alpha(brand.ink, 0.68) }}>
                  No breach databases were returned for this engagement.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </Box>
  );
};

export default DatabaseBreachesPage;
